import { Ruler } from 'lucide-react';
import { useIntl } from 'react-intl';
import ColorSwatch from '~/components/ColorSwatch';
import { CHECKERBOARD_TILE_SIZES_MM } from '~/domain/viewport/checkerboardTileSize';
import {
    FUSION_COLOR_MAPPINGS,
    pairGradient,
    PET_COLOR_MAPS,
} from '~/domain/viewport/fusionBlend';
import { formatMmAsCm } from '~/utils/units';
import ConsoleChoiceMenu from './ConsoleChoiceMenu';
import { ConsoleChoice } from './consoleChoiceItems';

/**
 * The swatch that stands for a colour choice in the bar and in its list:
 * wide enough to read as the picker's whole face beside the chevron.
 */
function Swatch({ background }: { readonly background: string }) {
    return <ColorSwatch background={background} width={24} height={12} />;
}

const TILE_SIZE_CHOICES: ReadonlyArray<ConsoleChoice<number>> =
    CHECKERBOARD_TILE_SIZES_MM.map((sizeMm) => ({
        value: sizeMm,
        label: `${formatMmAsCm(sizeMm, 1)} cm`,
    }));

interface TileSizeMenuProps {
    readonly sizeMm: number;
    readonly onSelect: (sizeMm: number) => void;
}

/** The checkerboard tile in force, in cm, and the sizes it can take. */
export function TileSizeMenu({ sizeMm, onSelect }: TileSizeMenuProps) {
    const intl = useIntl();
    return (
        <ConsoleChoiceMenu
            name={intl.formatMessage({ id: 'fusion.checkerboardTileSize' })}
            testId="blend-tile-size-trigger"
            appearance="picker"
            glyph={<Ruler size={12} />}
            menuAlign="right"
            choices={TILE_SIZE_CHOICES}
            value={sizeMm}
            onSelect={onSelect}
        />
    );
}

const FUSION_COLOR_CHOICES: ReadonlyArray<ConsoleChoice<string>> =
    FUSION_COLOR_MAPPINGS.map((mapping) => ({
        value: mapping.id,
        label: mapping.name,
        icon: (
            <Swatch
                background={pairGradient(
                    mapping.previewPrimary,
                    mapping.previewSecondary
                )}
            />
        ),
    }));

interface FusionColorMenuProps {
    readonly colorMappingId: string;
    readonly onSelect: (colorMappingId: string) => void;
}

/** The colour pair a non-PET fusion is tinted with, and the pairs offered. */
export function FusionColorMenu({
    colorMappingId,
    onSelect,
}: FusionColorMenuProps) {
    const intl = useIntl();
    return (
        <ConsoleChoiceMenu
            name={intl.formatMessage({ id: 'fusion.colors' })}
            testId="blend-color-trigger"
            appearance="swatch"
            menuAlign="right"
            choices={FUSION_COLOR_CHOICES}
            value={colorMappingId}
            onSelect={onSelect}
        />
    );
}

const PET_COLOR_CHOICES: ReadonlyArray<ConsoleChoice<number>> =
    PET_COLOR_MAPS.map((colorMap) => ({
        value: colorMap.id,
        label: colorMap.name,
        icon: <Swatch background={colorMap.previewGradient} />,
    }));

interface PetColorMapMenuProps {
    readonly petColorMapId: number;
    readonly onSelect: (petColorMapId: number) => void;
}

/** The ramp the pair's PET image is drawn through, and the ramps offered. */
export function PetColorMapMenu({
    petColorMapId,
    onSelect,
}: PetColorMapMenuProps) {
    const intl = useIntl();
    return (
        <ConsoleChoiceMenu
            name={intl.formatMessage({ id: 'fusion.petColorMap' })}
            testId="blend-pet-color-trigger"
            appearance="swatch"
            menuAlign="right"
            choices={PET_COLOR_CHOICES}
            value={petColorMapId}
            onSelect={onSelect}
        />
    );
}
